import { type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { PrintDomainScreenBackground } from "@boundary/components/printDomainScreenBackground";
import { ReferralDomainScreenBackground } from "@boundary/components/referralDomainScreenBackground";
import { LinkDomainScreenBackground } from "@boundary/components/linkDomainScreenBackground";
import { MainScreenBackground } from "@boundary/components/mainScreenBackground";
import { SubScreenBackground } from "@boundary/components/subScreenBackground";
import { DomainScreenBackground } from "@boundary/components/domainScreenBackground";
import { PaymentDomainScreenBackground } from "@boundary/components/paymentDomainScreenBackground";
import { BottomGradient } from "@boundary/components/bottomGradient";
import { Button } from "@boundary/components/button";
import styles from "./pageLayout.module.scss";

// ============================================================================
// Page - корень экрана
// ============================================================================

export type PageScreenBackground =
  | "hub"
  | "printDomain"
  | "sub"
  | "referralDomain"
  | "linkDomain"
  | "domain"
  | "paymentDomain"
  | "none";

interface PageProps {
  className?: string;
  /** Тема полноэкранного фона; по умолчанию базовый domain */
  screenBackground?: PageScreenBackground;
  withFog?: boolean;
  children: React.ReactNode;
}

function PageLayoutComponent({
  className,
  children,
  screenBackground = "domain",
  withFog
}: PageProps) {
  return (
    <>
      {screenBackground === "hub" && <MainScreenBackground />}
      {screenBackground === "printDomain" && <PrintDomainScreenBackground />}
      {screenBackground === "sub" && <SubScreenBackground />}
      {screenBackground === "referralDomain" && <ReferralDomainScreenBackground />}
      {screenBackground === "linkDomain" && <LinkDomainScreenBackground />}
      {screenBackground === "domain" && <DomainScreenBackground />}
      {screenBackground === "paymentDomain" && <PaymentDomainScreenBackground />}
      {withFog && <BottomGradient />}
      <main className={`${styles.page}${className ? ` ${className}` : ""}`}>
        {children}
      </main>
    </>
  );
}

// ============================================================================
// Page.SafeArea - резерв пространства
// ============================================================================

function PageSafeArea() {
  return <div className={styles.safeArea} />;
}

// ============================================================================
// Page.DisplayZone - viewport пользователя
// ============================================================================

interface DisplayZoneProps {
  className?: string;
  children: React.ReactNode;
}

function PageDisplayZone({ className, children }: DisplayZoneProps) {
  return (
    <div className={`${styles.displayZone}${className ? ` ${className}` : ""}`}>
      {children}
    </div>
  );
}

// ============================================================================
// BackButton - внутренний компонент
// ============================================================================

interface BackButtonProps {
  onBackClick?: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
}

function BackButton({ onBackClick }: BackButtonProps) {
  const navigate = useNavigate();

  const handleClick = (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    console.log(`[Button Click] BackButton: "Назад"`);
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (onBackClick) {
      onBackClick(e);
      return;
    }
    navigate(-1);
  };

  return (
    <Button
      onClick={handleClick}
      size="icon"
      variant="secondary"
      className={styles.backButton}
    >
      <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0.292893 7.29289C-0.0976315 7.68342 -0.0976315 8.31658 0.292893 8.70711L6.65685 15.0711C7.04738 15.4616 7.68054 15.4616 8.07107 15.0711C8.46159 14.6805 8.46159 14.0474 8.07107 13.6569L2.41421 8L8.07107 2.34315C8.46159 1.95262 8.46159 1.31946 8.07107 0.928932C7.68054 0.538408 7.04738 0.538408 6.65685 0.928932L0.292893 7.29289ZM14 8V7L1 7V8V9L14 9V8Z" fill="white"/>
      </svg>
    </Button>
  );
}

interface HeaderButtonProps {
  onClick?: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  ariaLabel?: string;
  children: React.ReactNode;
}

/**
 * Боковая кнопка шапки.
 *
 * Тот же размер и тот же вид, что у кнопки «назад»: обе стоят по краям шапки и
 * должны выглядеть парой. Отличается только знаком внутри — его передаёт экран.
 */
function HeaderButton({ onClick, ariaLabel, children }: HeaderButtonProps) {
  const handleClick = (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    onClick?.(e);
  };

  return (
    <Button
      onClick={handleClick}
      size="icon"
      variant="secondary"
      className={styles.backButton}
      aria-label={ariaLabel}
    >
      {children}
    </Button>
  );
}

// ============================================================================
// Page.DisplayZone.Header - навигация
// ============================================================================

interface DisplayZoneHeaderProps {
  className?: string;
  back?: boolean;
  onBackClick?: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  left?: React.ReactNode;
  center?: React.ReactNode;
  right?: React.ReactNode;
  fullCenter?: boolean;
}

function DisplayZoneHeader({
  className,
  back,
  onBackClick,
  left,
  center,
  right,
  fullCenter
}: DisplayZoneHeaderProps) {
  const headerClasses = [
    styles.displayZoneHeader,
    styles.displayZoneHeaderInline,
    fullCenter && styles.displayZoneHeaderFullCenter,
    className
  ]
    .filter(Boolean)
    .join(" ");

  const leftSlot = left ?? (back ? <BackButton onBackClick={onBackClick} /> : null);

  return (
    <div className={headerClasses}>
      <div className={styles.headerLeft}>
        {leftSlot ?? <div className={styles.headerSidePlaceholder} />}
      </div>
      <div className={styles.headerCenter}>
        {center}
      </div>
      {!fullCenter && (
        <div className={styles.headerRight}>
          {right ?? <div className={styles.headerSidePlaceholder} />}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Page.DisplayZone.Body - поток документа
// ============================================================================

interface DisplayZoneBodyProps {
  className?: string;
  padded?: boolean;
  children: React.ReactNode;
}

function DisplayZoneBody({
  padded = true,
  className,
  children
}: DisplayZoneBodyProps) {
  const bodyClasses = [
    styles.displayZoneBody,
    padded && styles.displayZoneBodyPadded,
    className
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={bodyClasses}>
      {children}
    </div>
  );
}

// ============================================================================
// Page.DisplayZone.Sticky - нижний слой
// ============================================================================

interface DisplayZoneStickyProps {
  className?: string;
  variant?: "sticky" | "static" | "hidden";
  children: React.ReactNode;
}

function DisplayZoneSticky({
  variant = "sticky",
  className,
  children
}: DisplayZoneStickyProps) {
  if (variant === "hidden") {
    return null;
  }

  const stickyClasses = [
    styles.displayZoneSticky,
    variant === "sticky" && styles.displayZoneStickySticky,
    variant === "static" && styles.displayZoneStickyStatic,
    className
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={stickyClasses}>
      {children}
    </div>
  );
}

// ============================================================================
// Экспорт
// ============================================================================

export const Page = Object.assign(PageLayoutComponent, {
  SafeArea: PageSafeArea,
  DisplayZone: Object.assign(PageDisplayZone, {
    Header: Object.assign(DisplayZoneHeader, {
      // Боковая кнопка шапки: тот же вид, что у «назад». Экран передаёт только
      // знак и действие, а размер и оформление берутся отсюда.
      Button: HeaderButton
    }),
    Body: DisplayZoneBody,
    Sticky: DisplayZoneSticky
  })
});
